import { NgModule, APP_INITIALIZER, isDevMode, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ToastModule } from 'primeng/toast';
import {
  provideKeycloak,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  KeycloakService,
  includeBearerTokenInterceptor,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';
import { ApiModule, Configuration, ConfigurationParameters } from "@mobiletract/esg-calculator-service-angular-client";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { DateInterceptor } from './shared/interceptors/date-interceptor';
import { LanguageInterceptor } from './shared/interceptors/language-interceptor';
import { httpErrorInterceptor } from './shared/interceptors/http-error.interceptor';
import { SharedModule } from './shared/shared.module';
import { companyContextReducer, localStorageMetaReducer } from './core/state/company/company-context.reducer';
import { Router } from '@angular/router';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CompanyContextEffects } from './core/state/company/company-context.effects';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { MessageModule } from 'primeng/message';
import { AppPanelComponent } from "./shared/components/app-panel/app-panel.component";
import { ConferenceFormModule } from "./features/conference-form/conference-form.module";
import { SummaryEffects } from './core/state/summary/summary.effects';
import { UserEffects } from './core/state/user/user.effects';
import { summaryReducer } from './core/state/summary/summary.reducer';
import { version } from '../../package.json';

import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import * as Sentry from '@sentry/angular';

import { QRCodeModule } from 'angularx-qrcode';
import { userReducer } from './core/state/user/user.reducer';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { NgxEchartsModule } from 'ngx-echarts';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { providePrimeNG } from 'primeng/config';
import {
  ekowitrynaPreset,
  EkoLayoutAppBarComponent,
  EkoLayoutSidebarComponent,
  EkoNavigationMenuComponent,
  MultiTranslateHttpLoader,
  provideEkoHostAppMetadata
} from '@ekowitryna/ui';
import {
  ApiModule as AuthServiceApiModule,
  Configuration as AuthServiceConfiguration,
  ConfigurationParameters as AuthServiceConfigurationParameters
} from '@mobiletract/auth-service-angular-client';
import {
  ApiModule as AccountServiceApiModule,
  Configuration as AccountServiceConfiguration,
  ConfigurationParameters as AccountServiceConfigurationParameters
} from '@mobiletract/account-service-angular-client';
import { TranslateService } from '@ngx-translate/core';
import { provideEkoPreferencesSync } from './core/providers/eko-dark-mode.providers';
import { provideEkoBrowserTitle } from '@ekowitryna/ui';
import { GroupCacheInterceptor } from './shared/interceptors/group-cache.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json?version=' + version },
    { prefix: './assets/ui/i18n/', suffix:  '.json?version=' + version },
  ]);
}

export function authApiConfigFactory(): AuthServiceConfiguration {
  const keycloak = inject(Keycloak);
  return new AuthServiceConfiguration({
    basePath: `${environment.apiUrl}uaa`,
    credentials: {
      'bearer-jwt': () => keycloak.token ?? '',
    },
  });
}

export function accountApiConfigFactory(): AccountServiceConfiguration {
  const params: AccountServiceConfigurationParameters = {
    basePath: `${environment.apiUrl}accounts`,
  };
  return new AccountServiceConfiguration(params);
}

// Keycloak bearer token interceptor configuration
const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(`^${environment.apiUrl.replace(/\/$/, '')}.*`),
  bearerPrefix: 'Bearer'
});

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        AppPanelComponent,
        ToastModule,
        MessageModule,
        FormsModule,
        ReactiveFormsModule,
        ConferenceFormModule,
        SharedModule,
        DrawerModule,
        TooltipModule,
        ButtonModule,
        ChipModule,
        ZXingScannerModule,
        ConfirmPopupModule,
        EkoLayoutAppBarComponent,
        EkoLayoutSidebarComponent,
        EkoNavigationMenuComponent,
        AuthServiceApiModule.forRoot(authApiConfigFactory),
        AccountServiceApiModule.forRoot(accountApiConfigFactory),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        StoreModule.forRoot({
            companyContext: companyContextReducer,
            summary: summaryReducer,
            user: userReducer
        }, {
            metaReducers: [
                localStorageMetaReducer,
            ]
        }),
        EffectsModule.forRoot([]),
        ApiModule,
        EffectsModule.forFeature([CompanyContextEffects, SummaryEffects, UserEffects]),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
        GoogleTagManagerModule,
        QRCodeModule,
        NgxEchartsModule.forRoot({
            echarts: () => import('echarts')
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })], providers: [
        providePrimeNG({
            theme: {
                preset: ekowitrynaPreset,
                options: {
                    darkModeSelector: '.dark',
                    /* Bez warstwy – style PrimeNG (outlined w menu profilu) mają pierwszeństwo nad Tailwind */
                    cssLayer: false
                }
            }
        }),
        MessageService,
        ConfirmationService,
        provideEkoHostAppMetadata({
          name: 'ekowitryna-esq-calculator',
          version: version
        }),
        provideAnimationsAsync(),
        { provide: 'googleTagManagerId', useValue: environment.googleTagManagerId },
        { provide: 'googleTagManagerPreview', useValue: environment.googleTagManagerPreview },
        { provide: 'googleTagManagerAuth', useValue: environment.googleTagManagerAuth },
        {
          provide: Configuration,
          useFactory: esgApiConfigFactory
        },
        {
          provide: AuthServiceConfiguration,
          useFactory: authApiConfigFactory,
        },
        { provide: HTTP_INTERCEPTORS, useClass: DateInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: GroupCacheInterceptor, multi: true },
        provideKeycloak({
            config: {
                url: environment.keycloakUrl,
                realm: environment.realm,
                clientId: environment.clientId
            },
            initOptions: {
                onLoad: 'check-sso',
                checkLoginIframe: false,
                silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`
            },
            providers: [
                UserActivityService,
                AutoRefreshTokenService
            ],
            features: [
                withAutoRefreshToken({
                  onInactivityTimeout: 'login'
                })
            ]
        }),
        KeycloakService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        }, {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [urlCondition]
        },
        provideHttpClient(
          withInterceptors([includeBearerTokenInterceptor, httpErrorInterceptor]),
          withInterceptorsFromDi()
        ),
        ...provideEkoPreferencesSync(),
        provideEkoBrowserTitle('app.browserTitle'),
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA] })
export class AppModule { }

export function esgApiConfigFactory (): Configuration {
  const keycloak = inject(Keycloak);
  const params: ConfigurationParameters = {
    basePath: `${environment.apiUrl}esg`,
    credentials: {
      'bearer': () => {
        const token = keycloak?.token;
        return token || undefined;
      }
    }
  };
  return new Configuration(params);
}
